import { EMAIL_MARKETING_SOURCES } from '../../constants';
import {
    IAddOrUpdateUserOptions,
    IUpdateCartOptions,
    IEspClient,
} from '../../IEspClient';

import {
    SAILTHRU_INTEGRATION_ACTIONS,
    SAILTHRU_TRACK_ACTIONS,
    SAILTHRU_SCRIPT_TAG_ID,
} from './constants';
import { ISailthruClient } from './types';

export class SailthruClient implements IEspClient {
    private static instance: SailthruClient;

    private sailthruApiURL: string;
    private sailthruCustomerID: string;
    private sailthruInitialization: Promise<void>;

    private constructor (apiURL: string, customerID: string) {
        this.sailthruApiURL = apiURL;
        this.sailthruCustomerID = customerID;

        this.sailthruInitialization = this.loadAndInitSailthru();
    }

    public static get (options: ISailthruClient): SailthruClient {
        const { apiURL, customerID }: ISailthruClient = options;

        if (!apiURL) {
            throw new Error('[SailthruClient] Missing SAILTHRU_API_URL');
        }

        if (!customerID) {
            throw new Error('[SailthruClient] Missing SAILTHRU_CUSTOMER_ID');
        }

        if (SailthruClient.instance) {
            return SailthruClient.instance;
        }

        SailthruClient.instance = new SailthruClient(apiURL, customerID);

        return SailthruClient.instance;
    }

    private static sailthruLoaded (): boolean {
        return window.Sailthru && typeof(window.Sailthru.init) === 'function';
    }

    private loadAndInitSailthru (): Promise<void> {
        return this.loadSailthru()
            .then((): void => {
                this.initSailthru();
            });
    }

    private async loadSailthru (): Promise<void> {
        return new Promise((resolve: () => void, reject: (error: any) => void): void  => {
            try {
                const scriptEl: HTMLScriptElement = document.createElement('script');

                scriptEl.src = this.sailthruApiURL;
                scriptEl.async = true;
                scriptEl.id = SAILTHRU_SCRIPT_TAG_ID;

                document.body.appendChild(scriptEl);

                scriptEl.addEventListener('load', (): void => {
                    resolve();
                });

                scriptEl.addEventListener('error', (): void => {
                    reject(new Error('[SailthruClient] error loading Sailthru lib.'));
                });
            } catch (err) {
                reject(err);
            }
        });
    }

    private initSailthru (): void {
        if (!SailthruClient.sailthruLoaded()) {
            throw new Error('[SailthruClient] cannot initialize as Sailthru lib not loaded.');
        }

        window.Sailthru.init({ customerId: this.sailthruCustomerID });
    }

    public async updateCart (options: IUpdateCartOptions): Promise<undefined> {
        const { email, cartItems, onSuccess, onError }: IUpdateCartOptions = options;

        if (!email) {
            return Promise.reject(new Error('[SailthruClient.updateCart] email required.'));
        }

        try {
            await this.sailthruInitialization;

            return window.Sailthru.integration(SAILTHRU_INTEGRATION_ACTIONS.PURCHASE, {
                incomplete: 1,
                key: 'email',
                email,
                items: cartItems,
                onSuccess,
                onError,
            });
        } catch (err) {
            return Promise.reject(new Error(`[SailthruClient.updateCart] ${err}`));
        }
    }

    public async addOrUpdateUser (options: IAddOrUpdateUserOptions): Promise<undefined> {
        let userSource: string | undefined;

        const {
            email,
            userLists,
            vars,
            source,
            onSuccess,
            onError,
        }: IAddOrUpdateUserOptions = options;

        if (!email) {
            return Promise.reject(new Error('[SailthruClient.addOrUpdateUser] email required.'));
        }

        userSource = source;

        if (!userSource && !userLists) {
            userSource = EMAIL_MARKETING_SOURCES.WEB;
        }

        try {
            await this.sailthruInitialization;

            return window.Sailthru.integration(SAILTHRU_INTEGRATION_ACTIONS.USER_SIGN_UP, {
                email,
                key: 'email',
                lists: userLists,
                source: userSource,
                vars,
                onSuccess,
                onError,
            });
        } catch (err) {
            return Promise.reject(new Error(`[SailthruClient.addOrUpdateUser] ${err}`));
        }
    }

    public async disableUserTracking (): Promise<void> {
        try {
            await this.sailthruInitialization;

            window.Sailthru.track(SAILTHRU_TRACK_ACTIONS.DO_NOT_TRACK_USER);

            return Promise.resolve();
        } catch (err) {
            return Promise.reject(new Error(`[SailthruClient.disableUserTracking] ${err}`));
        }
    }

    public async enableUserTracking (): Promise<void> {
        try {
            await this.sailthruInitialization;

            window.Sailthru.track(SAILTHRU_TRACK_ACTIONS.DO_NOT_TRACK_USER, {
                set: false,
            });

            return Promise.resolve();
        } catch (err) {
            return Promise.reject(new Error(`[SailthruClient.enableUserTracking] ${err}`));
        }
    }
}
